import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Upload, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { addEvent, getEventById, updateEvent } from '../../services/eventService';
import { Event } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../hooks/useCategories';
import { useCities } from '../../hooks/useCities';
import CategoryIcon from '../../components/CategoryIcon';

const EventForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEditMode = !!id;
  const { t } = useTranslation();
  const { categories, loading: loadingCategories } = useCategories();
  const { cities, loading: loadingCities } = useCities();

  const [formData, setFormData] = useState<Omit<Event, 'id'>>({
    title: '',
    description: '',
    imageUrl: '',
    venue: '',
    city: '',
    date: '',
    time: '',
    category: '',
    ticketUrl: ''
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    const fetchEvent = async () => {
      if (isEditMode && id) {
        try {
          setLoading(true);
          const eventData = await getEventById(id);
          if (eventData) {
            setFormData({
              title: eventData.title,
              description: eventData.description,
              imageUrl: eventData.imageUrl,
              venue: eventData.venue,
              city: eventData.city,
              date: eventData.date,
              time: eventData.time,
              price: eventData.price,
              category: eventData.category,
              availableTickets: eventData.availableTickets,
              ticketUrl: eventData.ticketUrl || ''
            });
            setImagePreview(eventData.imageUrl);
          }
        } catch (err) {
          console.error('Error fetching event:', err);
          setError(t('error'));
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEvent();
  }, [id, isAdmin, isEditMode, navigate, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle numeric values
    if (name === 'price' || name === 'availableTickets') {
      const numValue = value === '' ? undefined : parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];

    const allowed = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setError('Formato inválido. Use JPEG, PNG ou GIF.');
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo 2MB.');
      e.target.value = '';
      return;
    }

    setError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.description || !formData.venue || 
        !formData.city || !formData.date || !formData.time || !formData.category) {
      setError(t('admin.eventForm.fillAllFields'));
      return;
    }
    
    if (!imageFile && !formData.imageUrl) {
      setError(t('admin.eventForm.uploadImage'));
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (isEditMode && id) {
        await updateEvent(id, formData, imageFile);
        setSuccess(t('admin.eventForm.eventUpdated'));
      } else {
        await addEvent(formData, imageFile);
        setSuccess(t('admin.eventForm.eventCreated'));
        
        // Reset form after successful creation
        setFormData({
          title: '',
          description: '',
          imageUrl: '',
          venue: '',
          city: '',
          date: '',
          time: '',
          category: '',
          ticketUrl: ''
        });
        setImageFile(null);
        setImagePreview('');
      }
      
      // Navigate back to admin dashboard after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
    } catch (err) {
      console.error('Error submitting event:', err);
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-indigo-600 hover:text-indigo-800"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {t('admin.eventForm.back')}
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          {isEditMode ? t('admin.eventForm.editEvent') : t('admin.eventForm.createEvent')}
        </h1>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.eventTitle')} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.description')} *
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label htmlFor="venue" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.venue')} *
              </label>
              <input
                type="text"
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.city')} *
              </label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">{t('admin.eventForm.selectCity')}</option>
                {loadingCities ? (
                  <option value="" disabled>Loading cities...</option>
                ) : (
                  cities.map((city) => (
                    <option key={city.id} value={city.name}>{city.name}</option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.date')} *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.time')} *
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.category')} *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">{t('admin.eventForm.selectCategory')}</option>
                {loadingCategories ? (
                  <option value="" disabled>Loading categories...</option>
                ) : (
                  categories.map((category) => (
                    <option key={category.id} value={category.slug} className="capitalize">
                      {category.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.price')}
              </label>
              <input
                type="number"
                id="price"
                name="price"
                min="0"
                step="0.01"
                value={formData.price || ''}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="availableTickets" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.availableTickets')}
              </label>
              <input
                type="number"
                id="availableTickets"
                name="availableTickets"
                min="0"
                value={formData.availableTickets || ''}
                onChange={handleChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="ticketUrl" className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.eventForm.ticketUrl')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LinkIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="url"
                  id="ticketUrl"
                  name="ticketUrl"
                  value={formData.ticketUrl}
                  onChange={handleChange}
                  placeholder={t('admin.eventForm.ticketUrlPlaceholder')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {t('admin.eventForm.ticketUrlHelp')}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.eventForm.eventImage')} *
              </label>

              {/* Preview na proporção 1600×838 */}
              <div className="w-full aspect-[1600/838] rounded-xl overflow-hidden bg-surface-container mb-3 border border-outline-variant">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-on-surface-variant">
                    <Upload className="h-10 w-10" />
                    <span className="font-headline text-label-md">Prévia da capa do evento</span>
                  </div>
                )}
              </div>

              <label
                htmlFor="image-upload"
                className="inline-flex items-center gap-2 cursor-pointer bg-primary text-on-primary px-5 py-2.5 rounded-lg font-headline text-label-md hover:brightness-110 transition-all active:scale-95"
              >
                <Upload className="h-4 w-4" />
                {imagePreview ? 'Trocar imagem' : t('admin.eventForm.uploadImage')}
                <input
                  id="image-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                  onChange={handleImageChange}
                  className="sr-only"
                />
              </label>
              <p className="mt-2 font-body text-label-sm text-on-surface-variant">
                JPEG, GIF ou PNG · Máximo 2MB · Recomendado: 1600 × 838 px
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
            >
              {t('admin.eventForm.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md shadow-sm text-sm font-medium focus:outline-none flex items-center"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {isEditMode ? t('admin.eventForm.updating') : t('admin.eventForm.creating')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? t('admin.eventForm.update') : t('admin.eventForm.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;